import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Typography, Stack } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { navigationTranslationState } from 'store';
import {
  Wrapper,
  AnimatedImage,
  LoaderContainer,
} from '@/components/navigation/NavigationIntroScreen/styles';
import { ProgressBar } from '@/components/navigation/NavigationIntroScreen/components/ProgressBar';
import DeviceManager from '@/components/util/deviceManager';
import * as all from '@/components/util/all';
import { Tour } from '@/_models/Tour';
import { getAsset } from '@/utils/getAsset';

interface NavigationIntroScreenProps {
  appState: any;
  tour: Tour;
  onLoaded: () => void;
  logo: string;
  rtl?: boolean;
}

export const NavigationIntroScreen: React.FC<NavigationIntroScreenProps> = ({
  appState,
  tour,
  onLoaded = () => {},
  logo: footerLogo,
  rtl,
}) => {
  const [animateLogo, setAnimateLogo] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loaderVisible, setLoaderVisible] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [choicePopupOpen, setChoicePopupOpen] = useState(false);
  const translation = useRecoilValue(navigationTranslationState);

  const progressRef = useRef(progress);
  const appStateRef = useRef(appState);
  const isMobile = DeviceManager.isMobile();

  useEffect(() => {
    setTimeout(() => {
      setAnimateLogo(true);
    }, 1000);

    if (appState === 'playing') {
      setLoaderVisible(true);
      setProgress(100);
      onLoaded();
    } else {
      setTimeout(() => {
        setLoaderVisible(true);
        if (appStateRef.current === 'playing') {
          setProgress(100);
          onLoaded();
        } else {
          const intervalId = setInterval(() => {
            if (progressRef.current === 100) {
              clearInterval(intervalId);
              onLoaded();
              return;
            }
            setProgress(progressRef.current + 20);
          }, 1000);
        }
      }, 1200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  useEffect(() => {
    appStateRef.current = appState;
  }, [appState]);

  const progressText = useMemo(() => {
    if (progress > 75) return translation?.navWillStartSoon || 'Your navigation will start soon';
    if (progress > 50) return translation?.navCalculatingPath || 'Calculating the path';
    if (progress > 25) return translation?.navLoadingWaypoints || 'Loading waypoints';
    return translation?.navLoading3dSpace || 'Loading the 3D space';
  }, [progress, translation]);

  const userLogo = tour.user.whitelabelId
    ? `${process.env.NEXT_PUBLIC_CDN}/users/${tour.user.id}/logo.png${all.getVersion(
        tour.user.updatedAt,
      )}`
    : getAsset('logo-black.svg');
  const logo = tour.client?.logo
    ? `${process.env.NEXT_PUBLIC_CDN}/clients/${tour.client?.id}/logo.png${all.getVersion(
        tour.client?.updatedAt,
      )}`
    : userLogo;

  return (
    <Wrapper style={{ zIndex: 9999 }}>
      <AnimatedImage src={logo} animate={animateLogo} style={{ position: 'absolute' }} />
      <LoaderContainer style={{ opacity: loaderVisible ? 1 : 0 }}>
        <img
          src="/images/loading.gif"
          alt=""
          style={{ width: isMobile ? '65%' : '300px' }}
          onClick={() => {
            setPopupOpen(!popupOpen);
          }}
        />
        <ProgressBar progress={progress} sx={{ marginTop: '16px' }} />
        <Typography
          variant="h4"
          fontWeight={600}
          lineHeight="30px"
          sx={{ marginTop: '16px', direction: rtl ? 'rtl' : 'ltr' }}
          onClick={() => {
            setChoicePopupOpen(!choicePopupOpen);
          }}
        >
          {progressText}
        </Typography>
        <Typography
          color="#667085"
          lineHeight="20px"
          sx={{ marginTop: '10px', direction: rtl ? 'rtl' : 'ltr' }}
        >
          {translation?.navWillTakeUp10Sec || 'This will take up to 10 seconds'}
        </Typography>
      </LoaderContainer>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        alignSelf="center"
        sx={{
          width: '100%',
          gap: '7px',
          position: 'fixed',
          bottom: 0,
          // right: '20px',
          mt: '4px',
          mb: '20px',
          display: { xs: 'flex', sm: 'none' },
        }}
      >
        <Typography
          fontSize="16px"
          lineHeight="30px"
          fontWeight={400}
          sx={{ direction: rtl ? 'rtl' : 'ltr' }}
        >
          {translation?.navPowerBy || 'Powered by'}
        </Typography>
        <img className="watermark" width="81px" height="25.5px" alt="" src={footerLogo} />
      </Stack>
    </Wrapper>
  );
};
